import { useEffect, useMemo, useState } from 'react';
import { Quote, AlertCircle, ArrowUp, ArrowDown, Eye, EyeOff, Trash2, Pencil, Search } from 'lucide-react';
import { api, formatDate } from '../../lib/api';

const MAX_QUOTE = 600;

export default function AdminTestimonials() {
  const [featured, setFeatured] = useState(null);
  const [candidates, setCandidates] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState({ quote: '', attribution_name: '' });
  const [courseFilter, setCourseFilter] = useState('');
  const [search, setSearch] = useState('');
  const [hideFeatured, setHideFeatured] = useState(true);

  const load = () => Promise.all([
    api('/api/admin/testimonials').then((d) => setFeatured(d.testimonials)),
    api('/api/admin/testimonials/candidates').then((d) => setCandidates(d.candidates)),
  ]).catch((e) => setError(e.message || 'Failed to load testimonials.'));

  useEffect(() => { load(); }, []);

  const run = async (key, fn) => {
    setBusy(key);
    setError(null);
    try {
      await fn();
      await load();
      return true;
    } catch (e) {
      setError(e.message || 'Action failed.');
      return false;
    } finally {
      setBusy(null);
    }
  };

  const startFeature = (c) => {
    setEditing(`new:${c.id}`);
    setDraft({ quote: c.text.slice(0, MAX_QUOTE), attribution_name: c.suggested_attribution });
  };
  const startEdit = (t) => {
    setEditing(t.id);
    setDraft({ quote: t.quote, attribution_name: t.attribution_name });
  };

  const saveNew = (c) => run(`new:${c.id}`, () => api('/api/admin/testimonials', {
    method: 'POST',
    body: { evaluation_response_id: c.id, ...draft },
  })).then((ok) => ok && setEditing(null));

  const saveEdit = (t) => run(t.id, () => api(`/api/admin/testimonials/${t.id}`, {
    method: 'PATCH', body: draft,
  })).then((ok) => ok && setEditing(null));

  const patch = (t, body) => run(t.id, () => api(`/api/admin/testimonials/${t.id}`, { method: 'PATCH', body }));

  const remove = (t) => {
    if (!window.confirm('Take this quote off the landing page? The evaluation answer itself is kept.')) return;
    run(t.id, () => api(`/api/admin/testimonials/${t.id}`, { method: 'DELETE' }));
  };

  const move = (i, dir) => {
    const list = featured.slice();
    const j = i + dir;
    if (j < 0 || j >= list.length) return;
    [list[i], list[j]] = [list[j], list[i]];
    run(list[i].id, () => Promise.all(
      list.map((t, idx) => (t.display_order === idx ? null
        : api(`/api/admin/testimonials/${t.id}`, { method: 'PATCH', body: { display_order: idx } })))
    ));
  };

  const courses = useMemo(() => {
    const m = new Map();
    (candidates || []).forEach((c) => m.set(c.course_id, c.course_title));
    return [...m.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [candidates]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (candidates || []).filter((c) => (!courseFilter || c.course_id === courseFilter)
      && (!hideFeatured || !c.testimonial_id)
      && (!q || c.text.toLowerCase().includes(q) || (c.student_name || '').toLowerCase().includes(q)));
  }, [candidates, courseFilter, search, hideFeatured]);

  const published = (featured || []).filter((t) => t.is_published).length;

  return (
    <div className="p-4 lg:p-8 max-w-5xl mx-auto">
      <h1 className="text-2xl font-bold flex items-center gap-2 mb-2">
        <Quote size={22} /> Testimonials
      </h1>
      <p className="text-sm text-slate-500 mb-6">
        Pick written evaluation answers to show in “What Learners Say” on the home page. Nothing appears there until you
        feature it here. You can trim or edit the wording before it goes live; the original answer is kept for CE reporting.
      </p>

      {error && (
        <div className="mb-4 px-3 py-2 bg-rose-50 border border-rose-200 text-rose-700 rounded text-sm flex items-start gap-2">
          <AlertCircle size={14} className="shrink-0 mt-0.5" /> {error}
        </div>
      )}

      <section className="mb-10">
        <h2 className="text-lg font-semibold mb-1">On the home page</h2>
        <p className="text-sm text-slate-500 mb-3">
          {featured == null ? 'Loading…'
            : `${published} shown${featured.length > published ? `, ${featured.length - published} hidden` : ''}. The page shows up to 12, in this order. With none shown, the section is hidden.`}
        </p>
        {featured && !featured.length && (
          <div className="bg-white border border-dashed border-slate-300 rounded-lg px-4 py-8 text-center text-sm text-slate-500">
            No quotes featured yet. Pick one from the evaluation comments below.
          </div>
        )}
        <div className="space-y-3">
          {(featured || []).map((t, i) => (
            <div key={t.id} className={`bg-white border rounded-lg p-4 ${t.is_published ? 'border-slate-200' : 'border-slate-200 opacity-60'}`}>
              {editing === t.id ? (
                <Editor
                  draft={draft} setDraft={setDraft} original={t.original_text} courseTitle={t.course_title}
                  busy={busy === t.id} onCancel={() => setEditing(null)} onSave={() => saveEdit(t)}
                />
              ) : (
                <div className="flex gap-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-slate-700">“{t.quote}”</p>
                    <p className="mt-2 text-sm">
                      <span className="font-semibold text-slate-900">{t.attribution_name}</span>
                      {t.course_title && <span className="text-slate-500"> · {t.course_title}</span>}
                    </p>
                    <p className="mt-1 text-xs text-slate-400">
                      From {t.student_name}’s evaluation{t.quote !== t.original_text ? ' · edited' : ''}
                      {!t.is_published && ' · hidden'}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <div className="flex gap-1">
                      <IconBtn title="Move up" disabled={i === 0 || !!busy} onClick={() => move(i, -1)}><ArrowUp size={15} /></IconBtn>
                      <IconBtn title="Move down" disabled={i === featured.length - 1 || !!busy} onClick={() => move(i, 1)}><ArrowDown size={15} /></IconBtn>
                    </div>
                    <div className="flex gap-1">
                      <IconBtn title="Edit" disabled={!!busy} onClick={() => startEdit(t)}><Pencil size={15} /></IconBtn>
                      <IconBtn title={t.is_published ? 'Hide from home page' : 'Show on home page'} disabled={!!busy}
                        onClick={() => patch(t, { is_published: !t.is_published })}>
                        {t.is_published ? <EyeOff size={15} /> : <Eye size={15} />}
                      </IconBtn>
                      <IconBtn title="Remove" disabled={!!busy} onClick={() => remove(t)} danger><Trash2 size={15} /></IconBtn>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-lg font-semibold mb-3">Evaluation comments</h2>
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <div className="relative flex-1 min-w-[200px]">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search comments or names" className="input pl-8 w-full"
            />
          </div>
          <select value={courseFilter} onChange={(e) => setCourseFilter(e.target.value)} className="input w-auto max-w-xs">
            <option value="">All courses</option>
            {courses.map(([id, title]) => <option key={id} value={id}>{title}</option>)}
          </select>
          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={hideFeatured} onChange={(e) => setHideFeatured(e.target.checked)} />
            Hide featured
          </label>
        </div>

        {candidates == null ? (
          <p className="text-sm text-slate-500">Loading…</p>
        ) : !visible.length ? (
          <div className="bg-white border border-slate-200 rounded-lg px-4 py-8 text-center text-sm text-slate-500">
            {candidates.length ? 'No comments match these filters.' : 'No written evaluation answers yet.'}
          </div>
        ) : (
          <div className="space-y-3">
            {visible.map((c) => (
              <div key={c.id} className="bg-white border border-slate-200 rounded-lg p-4">
                {editing === `new:${c.id}` ? (
                  <Editor
                    draft={draft} setDraft={setDraft} original={c.text} courseTitle={c.course_title}
                    busy={busy === `new:${c.id}`} onCancel={() => setEditing(null)} onSave={() => saveNew(c)} isNew
                  />
                ) : (
                  <div className="flex gap-4">
                    <div className="flex-1 min-w-0">
                      {c.question_prompt && (
                        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-400 mb-1">
                          {c.question_prompt}
                          {c.choice && <span className="ml-2 normal-case tracking-normal text-slate-600">Answered {c.choice}</span>}
                        </p>
                      )}
                      <p className="text-slate-700 whitespace-pre-line">{c.text}</p>
                      <p className="mt-2 text-xs text-slate-500">
                        {c.student_name} · {c.course_title} · {formatDate(c.created_at)}
                      </p>
                    </div>
                    <div className="shrink-0">
                      {c.testimonial_id ? (
                        <span className="text-xs font-semibold text-emerald-700">
                          Featured{c.is_published ? '' : ' (hidden)'}
                        </span>
                      ) : (
                        <button
                          type="button" disabled={!!busy} onClick={() => startFeature(c)}
                          className="px-3 py-1.5 text-sm font-semibold rounded-md bg-brand text-white hover:bg-brand-dark disabled:opacity-50"
                        >
                          Feature
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function Editor({ draft, setDraft, original, courseTitle, busy, onCancel, onSave, isNew }) {
  const tooLong = draft.quote.length > MAX_QUOTE;
  const canSave = draft.quote.trim() && draft.attribution_name.trim() && !tooLong && !busy;
  return (
    <div>
      <label className="block text-xs font-semibold text-slate-500 mb-1">Quote as shown on the home page</label>
      <textarea
        value={draft.quote} rows={4} autoFocus
        onChange={(e) => setDraft({ ...draft, quote: e.target.value })}
        className="input w-full"
      />
      <div className={`text-xs mt-1 ${tooLong ? 'text-rose-600' : 'text-slate-400'}`}>
        {draft.quote.length}/{MAX_QUOTE}
        {draft.quote.trim() !== original && (
          <button type="button" className="ml-3 text-slate-600 hover:underline"
            onClick={() => setDraft({ ...draft, quote: original.slice(0, MAX_QUOTE) })}>
            Reset to the original answer
          </button>
        )}
      </div>
      <div className="mt-3 flex flex-wrap items-end gap-3">
        <div>
          <label className="block text-xs font-semibold text-slate-500 mb-1">Attribution</label>
          <input
            value={draft.attribution_name}
            onChange={(e) => setDraft({ ...draft, attribution_name: e.target.value })}
            className="input w-48"
          />
        </div>
        {courseTitle && <span className="text-sm text-slate-500 pb-2">· {courseTitle}</span>}
        <div className="flex-1" />
        <button type="button" onClick={onCancel} className="px-3 py-1.5 text-sm rounded-md border border-slate-300 hover:bg-slate-50">
          Cancel
        </button>
        <button
          type="button" disabled={!canSave} onClick={onSave}
          className="px-3 py-1.5 text-sm font-semibold rounded-md bg-brand text-white hover:bg-brand-dark disabled:opacity-50"
        >
          {busy ? 'Saving…' : isNew ? 'Feature on home page' : 'Save'}
        </button>
      </div>
    </div>
  );
}

function IconBtn({ children, danger, ...props }) {
  return (
    <button
      type="button" {...props}
      className={`p-1.5 rounded-md border border-slate-200 disabled:opacity-40 ${danger ? 'text-rose-600 hover:bg-rose-50' : 'text-slate-600 hover:bg-slate-50'}`}
    >
      {children}
    </button>
  );
}
